'use client';

import type {
    ButtonHTMLAttributes,
    HTMLAttributeAnchorTarget,
    MouseEventHandler,
} from 'react';
import { forwardRef } from 'react';
import type { ButtonProps as UIButtonsProps } from '@paypoteam/ui-components';
import {
    Button,
    buttonsFactoryVariants,
    buttonVariants,
    singleButtonVariant,
} from '@paypoteam/ui-components';
import type { VariantProps } from 'class-variance-authority';
import Link from 'next/link';

import { cn } from '@/lib/utils';

interface ButtonsProps extends UIButtonsProps {
    href?: string;
    dataTestId?: string;
    onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    target?: HTMLAttributeAnchorTarget;
}

export interface ButtonsFactoryProps
    extends ButtonHTMLAttributes<HTMLDivElement>,
        VariantProps<typeof buttonsFactoryVariants> {
    config: ButtonsProps[];
}

const ButtonsFactory = forwardRef<HTMLDivElement, ButtonsFactoryProps>(
    (
        {
            className,
            config,
            disableBreakpoints,
            justifyContent,
            reverseOnMobile,
            ...props
        },
        ref,
    ) => (
        <div
            className={cn(
                buttonsFactoryVariants({
                    disableBreakpoints,
                    justifyContent,
                    reverseOnMobile,
                }),
                className,
            )}
            ref={ref}
            {...props}
        >
            {config.map(
                (
                    {
                        href,
                        target,
                        onClick,
                        dataTestId,
                        size,
                        variant,
                        className: buttonClassName,
                        ...elem
                    },
                    i,
                ) =>
                    href ? (
                        <Link
                            key={`${href}_${i}`}
                            href={href}
                            target={target}
                            className={cn(
                                buttonVariants({ size, variant }),
                                singleButtonVariant({ disableBreakpoints }),
                                buttonClassName,
                            )}
                            onClick={onClick}
                            data-test-id={dataTestId}
                        >
                            {elem.children}
                        </Link>
                    ) : (
                        <Button
                            key={`button_${i}`}
                            className={cn(
                                buttonVariants({ size, variant }),
                                singleButtonVariant({ disableBreakpoints }),
                                buttonClassName,
                            )}
                            onClick={onClick}
                            data-test-id={dataTestId}
                            {...elem}
                        >
                            {elem.children}
                        </Button>
                    ),
            )}
        </div>
    ),
);

export default ButtonsFactory;
